import { AlertTriangle, AlertCircle, Search, Clock, TrendingDown } from "lucide-react";
import { useTranslation } from "@/i18n";

const PainPoints = () => {
  const { t } = useTranslation();

  const painPoints = [
    {
      icon: AlertCircle,
      titleKey: "painPoints.items.poorDescriptions.title",
      descKey: "painPoints.items.poorDescriptions.description",
      color: "text-red-400",
      bgColor: "bg-red-500/15",
    },
    {
      icon: Search,
      titleKey: "painPoints.items.inconsistentSeo.title",
      descKey: "painPoints.items.inconsistentSeo.description",
      color: "text-amber-400",
      bgColor: "bg-amber-500/15",
    },
    {
      icon: Clock,
      titleKey: "painPoints.items.noTime.title",
      descKey: "painPoints.items.noTime.description",
      color: "text-orange-400",
      bgColor: "bg-orange-500/15",
    },
    {
      icon: TrendingDown,
      titleKey: "painPoints.items.noConversions.title",
      descKey: "painPoints.items.noConversions.description",
      color: "text-rose-400",
      bgColor: "bg-rose-500/15",
    },
  ];

  return (
    <section id="problem" className="py-20 md:py-28 relative overflow-hidden bg-[#0f1117]">
      {/* Glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[rgba(79,60,255,0.08)] blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="max-w-6xl mx-auto">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 border border-white/10 text-white/80 text-sm font-medium">
              <AlertTriangle className="w-4 h-4" />
              {t('painPoints.badge')}
            </span>
          </div>

          {/* Title */}
          <div className="text-center mb-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight">
              {t('painPoints.titlePre')}{' '}
              <span className="text-red-400">{t('painPoints.titleHighlight')}</span>{' '}
              {t('painPoints.titlePost')}
            </h2>
          </div>

          {/* Subtitle */}
          <div className="text-center mb-14">
            <p
              className="text-lg text-white/60 [&_strong]:text-white [&_strong]:font-bold"
              dangerouslySetInnerHTML={{ __html: t('painPoints.subtitle') }}
            />
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-14">
            {painPoints.map((point, index) => (
              <div
                key={index}
                className="group p-6 rounded-2xl bg-white/[0.06] border border-white/10 hover:bg-white/10 transition-all duration-300 hover:-translate-y-1"
              >
                <div className={`w-12 h-12 rounded-xl ${point.bgColor} flex items-center justify-center mb-4 transition-transform group-hover:scale-110`}>
                  <point.icon className={`w-6 h-6 ${point.color}`} />
                </div>
                <h3 className="text-white font-bold text-base mb-2">
                  {t(point.titleKey)}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {t(point.descKey)}
                </p>
              </div>
            ))}
          </div>

          {/* Solution */}
          <div className="text-center p-8 rounded-2xl bg-white/[0.04] border border-white/10">
            <p
              className="text-xl md:text-2xl font-semibold text-white [&_strong]:text-primary"
              dangerouslySetInnerHTML={{ __html: t('painPoints.solution') }}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default PainPoints;
